"use client";

import { useRef } from "react";
import { BUTTON_SMALL } from "@/components/tool-kit/styles";
import {
  CoordinatePlane,
  PLOT_HEIGHT,
  PLOT_WIDTH,
  PlanePoint,
} from "@/components/tools/math/coordinate-plane";
import type { SLOPE_CALCULATOR_STRINGS } from "@/components/tools/slope-calculator";
import { useToolNumber } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { downloadPng } from "@/lib/tool-kit/download";
import { decimalMarkOf } from "@/lib/tool-kit/math/locale";
import {
  equalizeAspect,
  padRange,
  type Viewport,
} from "@/lib/tool-kit/math/plot";
import {
  formatStyled,
  type NumberStyle,
  type Rational,
  toNumberR,
} from "@/lib/tool-kit/math/rational";
import type { SlopeResult } from "@/lib/tool-kit/math/slope";
import { CHART_COLORS, CHART_INK } from "@/lib/tool-kit/palette";

/*
 * The slope calculator's graph: both points, the line through them across the whole
 * window, and the rise and run drawn as a dashed right angle. Units are equal on both
 * axes, so the line leaves at its true angle of inclination.
 */

type Strings = typeof SLOPE_CALCULATOR_STRINGS;

/** Both points with padding, the origin pulled in when it is close, units equal. */
function slopeWindow(result: SlopeResult): Viewport {
  const xs = [toNumberR(result.p1.x), toNumberR(result.p2.x)];
  const ys = [toNumberR(result.p1.y), toNumberR(result.p2.y)];
  const span = Math.max(Math.abs(xs[1] - xs[0]), Math.abs(ys[1] - ys[0]), 1);
  const near = (values: number[]) =>
    Math.min(...values.map(Math.abs)) <= span ? [...values, 0] : values;
  const [xMin, xMax] = padRange(
    Math.min(...near(xs)),
    Math.max(...near(xs)),
    0.25,
    span
  );
  const [yMin, yMax] = padRange(
    Math.min(...near(ys)),
    Math.max(...near(ys)),
    0.25,
    span
  );
  return equalizeAspect({ xMin, xMax, yMin, yMax }, PLOT_WIDTH, PLOT_HEIGHT);
}

/** The dashed right angle from point 1 across (run) and up or down (rise) to point 2. */
function RiseRun({
  points,
  labels,
}: {
  /** Pixel positions: x₁, y₁, x₂, y₂. */
  points: [number, number, number, number];
  labels: { rise: string; run: string };
}) {
  const [px1, py1, px2, py2] = points;
  return (
    <>
      <path
        d={`M${px1} ${py1} H${px2} V${py2}`}
        fill="none"
        stroke={CHART_COLORS[1]}
        strokeDasharray="6 4"
        strokeWidth={2}
      />
      <text
        fill={CHART_INK.text}
        fontSize={12}
        textAnchor="middle"
        x={(px1 + px2) / 2}
        y={py1 + (py2 <= py1 ? 16 : -8)}
      >
        {labels.run}
      </text>
      <text
        fill={CHART_INK.text}
        fontSize={12}
        textAnchor={px2 >= px1 ? "start" : "end"}
        x={px2 + (px2 >= px1 ? 6 : -6)}
        y={(py1 + py2) / 2}
      >
        {labels.rise}
      </text>
    </>
  );
}

export function SlopeGraph({
  result,
  strings,
  style,
}: {
  result: SlopeResult;
  strings: Strings;
  style: NumberStyle;
}) {
  const fmt = useToolNumber();
  const ref = useRef<SVGSVGElement>(null);
  const viewport = slopeWindow(result);
  const [x1, y1, x2, y2] = [
    result.p1.x,
    result.p1.y,
    result.p2.x,
    result.p2.y,
  ].map(toNumberR);
  const m = result.slope === null ? null : toNumberR(result.slope);
  const mark = decimalMarkOf(fmt);
  const pointText = (x: Rational, y: Rational) =>
    t(strings.point, {
      x: formatStyled(x, style, mark),
      y: formatStyled(y, style, mark),
    });
  return (
    <div className="space-y-3">
      <CoordinatePlane
        formatTick={(value, decimals) =>
          fmt(value, { maximumFractionDigits: decimals })
        }
        svgRef={ref}
        title={t(strings.graphTitle, {
          p1: pointText(result.p1.x, result.p1.y),
          p2: pointText(result.p2.x, result.p2.y),
        })}
        viewport={viewport}
      >
        {(scale) => (
          <>
            {m === null ? (
              <line
                stroke={CHART_COLORS[0]}
                strokeWidth={2.5}
                x1={scale.x(x1)}
                x2={scale.x(x1)}
                y1={scale.y(viewport.yMin)}
                y2={scale.y(viewport.yMax)}
              />
            ) : (
              <line
                stroke={CHART_COLORS[0]}
                strokeWidth={2.5}
                x1={scale.x(viewport.xMin)}
                x2={scale.x(viewport.xMax)}
                y1={scale.y(y1 + m * (viewport.xMin - x1))}
                y2={scale.y(y1 + m * (viewport.xMax - x1))}
              />
            )}
            {result.kind === "oblique" ? (
              <RiseRun
                labels={{
                  run: t(strings.runLabel, {
                    value: formatStyled(result.dx, style, mark),
                  }),
                  rise: t(strings.riseLabel, {
                    value: formatStyled(result.dy, style, mark),
                  }),
                }}
                points={[scale.x(x1), scale.y(y1), scale.x(x2), scale.y(y2)]}
              />
            ) : null}
            <PlanePoint
              color={CHART_COLORS[3]}
              cx={scale.x(x1)}
              cy={scale.y(y1)}
              label={pointText(result.p1.x, result.p1.y)}
            />
            <PlanePoint
              color={CHART_COLORS[3]}
              cx={scale.x(x2)}
              cy={scale.y(y2)}
              label={pointText(result.p2.x, result.p2.y)}
            />
          </>
        )}
      </CoordinatePlane>
      <button
        className={BUTTON_SMALL}
        onClick={() => {
          if (ref.current) {
            downloadPng("slope-graph.png", ref.current).catch(() => undefined);
          }
        }}
        type="button"
      >
        {strings.downloadPng}
      </button>
    </div>
  );
}
